import { Injectable } from '@angular/core';
import dayjs from 'dayjs';
import tz from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { v4 as uuidv4 } from 'uuid';
dayjs.extend(tz);
dayjs.extend(utc);

const EdmontonTimezoneCode = 'America/Edmonton';

@Injectable()
export class Utils {
  static generateUUID(): string {
    return uuidv4();
  }

  static isValidString(toValidateString?: string | null | undefined): boolean {
    return (
      toValidateString !== undefined &&
      toValidateString !== null &&
      toValidateString.trim().length > 0
    );
  }

  static isUnAuthorized(bodyMessage: string | null | undefined): boolean {
    return (
      this.isValidString(bodyMessage) &&
      bodyMessage?.toLowerCase() === 'unauthorized'
    );
  }

  static replaceAll(
    originalString: string,
    findText: string,
    replaceText: string
  ) {
    let newString = originalString;
    if (originalString?.indexOf(findText) > -1) {
      //need to do a replaceAll
      newString = originalString.split(findText).join(replaceText);
    }
    return newString;
  }

  static isTrue(value: string | null, option?: string): boolean {
    return option
      ? value?.toLowerCase().trim() === option.toLowerCase().trim()
      : value?.toLowerCase().trim() === 'true';
  }

  static isFalse(value: string | null, option?: string): boolean {
    return option
      ? value?.toLowerCase().trim() === option.toLowerCase().trim()
      : value?.toLowerCase().trim() === 'false';
  }

  static isChecked(value: string | null, option?: string): boolean {
    return option
      ? value?.toLowerCase().trim() === option.toLowerCase().trim()
      : value?.toLowerCase().trim() === 'checked';
  }

  static isUntouched(value: string | null): boolean {
    return value === null || value?.trim() === '';
  }

  static isDateCurrentOrPast(utcDate: Date | string) {
    const startOfDayCurrentDate = dayjs().startOf('day').toDate();
    const startOfDayToDate =
      typeof utcDate === 'string'
        ? dayjs(utcDate).toDate()
        : dayjs(utcDate.toISOString().split('T')[0]).toDate();
    return startOfDayToDate <= startOfDayCurrentDate;
  }

  static stripTypename<T>(obj: T): T {
    if (Array.isArray(obj)) {
      return obj.map(Utils.stripTypename) as unknown as T;
    } else if (obj !== null && typeof obj === 'object') {
      const result = { ...obj };
      if ('__typename' in result) delete result.__typename;

      for (const key in result) {
        result[key] = Utils.stripTypename(result[key]);
      }
      return result;
    }
    return obj;
  }

  static isNegativeOrFractional(value: number): boolean {
    return value < 0 || value % 1 !== 0;
  }

  static getEventValue(
    value: string,
    currentValue: number | undefined
  ): number | null | undefined | boolean {
    const n = Number(value);
    if (isNaN(n)) {
      return false;
    }
    return value.trim() === ''
      ? currentValue === undefined
        ? undefined
        : null
      : n;
  }
  /**
   * Transpose the provided date to 'America/Edmonton' (MST or MDT) timezone and format using the provided template.
   * @param date Date to be formatted. Now when undefined.
   * @param format Date format template.  ISO8601 when undefined.
   * @returns Formatted date.
   */
  static edmontonTime(date?: Date, format?: string): string {
    return dayjs(date).tz(EdmontonTimezoneCode).format(format);
  }
  // same as new Date(dobValue + ' 00:00') or dayjs(date).
  // dayjs(date).tz(EdmontonTimezoneCode) and dayjs.tz(date, EdmontonTimezoneCode) are different
  static toEdmontonTime(date: Date) {
    return dayjs(date).tz(EdmontonTimezoneCode);
  }
  static newDateWithEdmontonTime(dateTimeString: string) {
    return dayjs.tz(dateTimeString, EdmontonTimezoneCode).toDate();
  }
}
